'use strict';
const observable = require('@mindmup/mapjs-core/src/util/observable'),
	checkParams = require('@mindmup/js-utils/src/check-params'),
	MICROSOFT_TEAMS_IDENTITY = 'microsoft-teams',
	DEFAULT_TITLE = 'New Team MindMup',
	AUTH_SCOPES = 'User.Read Team.ReadBasic.All openid profile',
	EDITING_CONTEXT = '#editingContext=microsoft-teams%20compact';
module.exports = function MicrosoftTeamsModel({config, storage, navigatorShim, goldApi, microsoftTeamsShim, sanitiseMapTitle, defaultMapContent, s3Api, pkceUtils} = {}) {
	let mapTitle, mapTheme, selectedFile, saveAction;
	checkParams(
		{config, storage, navigatorShim, goldApi, microsoftTeamsShim, sanitiseMapTitle, defaultMapContent, s3Api, pkceUtils},
		['config', 'storage', 'navigatorShim', 'goldApi', 'microsoftTeamsShim', 'sanitiseMapTitle', 'defaultMapContent', 's3Api',  'pkceUtils']
	);
	if (!defaultMapContent || !s3Api || !config || !storage || !navigatorShim || !goldApi || !microsoftTeamsShim|| !sanitiseMapTitle || !pkceUtils) {
		throw new Error('invalid-args');
	}
	const obj = observable(this),
		isPrivateChannel = (context) => {
			return context?.channelType === 'Private';
		},
		siteOrigin = navigatorShim.getOrigin(),
		currentMapTitle = () => sanitiseMapTitle(mapTitle) || DEFAULT_TITLE,
		getAffiliateTeamIdentity = accountInfo => {
			const providerProfile = accountInfo.providerProfile,
				affiliateTeamIdentity = providerProfile && providerProfile.token && {
					provider: MICROSOFT_TEAMS_IDENTITY,
					token: providerProfile.token
				};
			return affiliateTeamIdentity;
		},
		sendContext = (context => {
			obj.dispatchEvent('context', context);
		}),
		sendProgressMessage = (message) => sendContext({message}),
		backgroundAuthenticate = async () => {
			const context = await microsoftTeamsShim.getContext(),
				identityToken = await microsoftTeamsShim.getIdentityToken(),
				defaultScopes = AUTH_SCOPES.split(' '),
				additionalScopes = isPrivateChannel(context) ? ['Channel.ReadBasic.All'].concat(defaultScopes) : defaultScopes;

			return goldApi.exec('authtoken/from_identity_token', {identityToken, additionalScopes});
		};

	obj.canUseTeams = (accountInfo) => {
		if (!accountInfo || !accountInfo.subscription || !accountInfo.subscription.actions) {
			return false;
		}
		if (!accountInfo.subscription.actions.includes('UseAffiliateTeams') || !accountInfo.subscription.actions.includes('ListGoldMaps')) {
			return false;
		}
		return true;
	};
	//TODO: remove and call microsoftTeamsShim directly
	obj.initialisationComplete = microsoftTeamsShim.initializationComplete;

	//TODO: remove and call microsoftTeamsShim directly
	obj.getContext = microsoftTeamsShim.getContext;

	obj.setMapTitle = (newMapTitle) => {
		mapTitle = newMapTitle;
	};
	obj.setMapTheme = (newMapTheme) => {
		mapTheme = newMapTheme;
	};

	obj.getExternalContextId = async (token) => {
		const context = await microsoftTeamsShim.getContext(),
			teamId = context?.hostTeamGroupId,
			channelId = context.channelId;
		return {token, teamId, channelId};
	};
	obj.startConfiguration = async (callParams) => {
		checkParams(callParams, ['accountInfo', 'role']);
		const {accountInfo, role} = callParams,
			createSharedLink = async (context, fileId) => {
				sendProgressMessage('Creating teams link, please wait...');
				const teamId = context?.hostTeamGroupId,
					fileKey = fileId + '.pointer',
					channelId = context.channelId,
					token = accountInfo.providerProfile.token,
					externalContextId = {token, teamId, channelId};

				return goldApi.exec('file/msteams/create', {license: accountInfo.license, fileKey, accessLevel: role, externalContextId});
			},
			shareWithSubTeam = async (context, fileId) => {
				sendProgressMessage('Sharing new map with team, please wait...');
				await goldApi.exec('file/share_with_subteam', {
					fileId: fileId,
					license: accountInfo.license,
					role: role,
					teamId: `microsoft-teams_${context.groupId}`
				});
			},
			makeMapAvailable = async (context, fileId, title) => {
				if (isPrivateChannel(context)) {
					const linkInfo = await createSharedLink(context, fileId),
						contentUrl = `${linkInfo.url}${EDITING_CONTEXT}`;
					sendProgressMessage('Storing tab settings, please wait...');
					await microsoftTeamsShim.mindmupSaveEventResult({origin: siteOrigin, fileId, title, contentUrl});
				} else {
					await shareWithSubTeam(context, fileId);
					sendProgressMessage('Storing tab settings, please wait...');
					await microsoftTeamsShim.mindmupSaveEventResult({origin: siteOrigin, fileId, title});

				}
			},
			createNewMap = async (context) => {
				sendProgressMessage('Creating new map, please wait...');
				const title = currentMapTitle(),
					uploadConfig = await goldApi.exec('file/upload_config', {
						fileId: `${title}.new`,
						license: accountInfo.license
					}),
					fileKey = uploadConfig.key,
					fileId = fileKey.split('/')[1],
					generateMapContent = () => {
						const mapContent = JSON.parse(JSON.stringify(defaultMapContent));
						if (mapTitle) {
							mapContent.ideas[1].title = mapTitle;
						}
						if (mapTheme) {
							mapContent.attr = {theme: mapTheme};
						}
						return JSON.stringify(mapContent);
					};
				await s3Api.save(generateMapContent(), uploadConfig);

				await makeMapAvailable(context, fileId, title);
			},
			openMap = async (context) => {
				const fileId = selectedFile.url.split('/')[1],
					title = sanitiseMapTitle(selectedFile.description);
				await makeMapAvailable(context, fileId, title);
			},
			onSave = async () => {
				const context = await microsoftTeamsShim.getContext();
				if (saveAction === 'openMap' && selectedFile) {
					return openMap(context);
				} else {
					return createNewMap(context);
				}


			};
		microsoftTeamsShim.registerOnSaveHandler(onSave);
	};

	obj.preCacheTeams = async accountInfo => {
		const license = accountInfo.license,
			affiliateTeamIdentity = getAffiliateTeamIdentity(accountInfo);

		return goldApi.exec('subteam/affiliate/cache_teams', {license, affiliateTeamIdentity});
	};
	obj.openEditor = async () => {
		const context = await microsoftTeamsShim.getContext();
		navigatorShim.redirect(`${siteOrigin}/map/_v2/${context.entityId}${EDITING_CONTEXT}`);
	};
	obj.openMicrosoftAuthPage = async () => {
		const context = await microsoftTeamsShim.getContext(),
			requiredScopes = isPrivateChannel(context) ? 'Channel.ReadBasic.All ' + AUTH_SCOPES : AUTH_SCOPES,
			{code, verifier, state} = await pkceUtils.asyncCreateChallenge(),
			redirectTo = pkceUtils.createPKCEURL({
				code,
				state,
				endpointUrl: `https://login.microsoftonline.com/${context.tid}/oauth2/v2.0/`,
				requiredScopes,
				optional: {login_hint: context.loginHint},
				redirectUri: `${siteOrigin}/microsoft-teams-authenticated.html`
			});
		storage.setItem('simple.state', state);
		storage.setItem('simple.verifier', verifier);
		navigatorShim.redirect(redirectTo);
	};
	obj.getAuthScopes = () => AUTH_SCOPES;
	obj.responseReceived = async () => {
		const pkceResponse = navigatorShim.getHashAsMap(),
			state = storage.getItem('simple.state'),
			verifier = storage.getItem('simple.verifier');
		try {
			if (!pkceResponse) {
				throw 'invalid-response';
			}
			const context = await microsoftTeamsShim.getContext(),
				endpointUrl = `https://login.microsoftonline.com/${context.tid}/oauth2/v2.0/`,
				redirectUri = `${siteOrigin}/microsoft-teams-authenticated.html`,
				authResponse = await pkceUtils.completePKCEFlow({pkceResponse, state, verifier, endpointUrl, requiredScopes: AUTH_SCOPES, redirectUri});
			if (!authResponse) {
				throw 'invalid-response';
			}
			if (authResponse.error || !authResponse.access_token) {
				microsoftTeamsShim.failAuthentication(authResponse.error);
			} else {
				await microsoftTeamsShim.completeAuthentication({authResponse});
			}
		} catch (e) {
			microsoftTeamsShim.failAuthentication();
		}
	};
	obj.authenticate = () => {
		return microsoftTeamsShim.authenticate(`${siteOrigin}/microsoft-teams-authentication.html`);
	};
	obj.errorReason = reason => {
		obj.dispatchEvent('error', reason);
	};
	obj.setSelectedFile = (fileEntry) => {
		selectedFile = fileEntry;
	};
	obj.setSaveAction = (action) => {
		saveAction = action;
	};
	Object.freeze(Object.assign(this, {isPrivateChannel, backgroundAuthenticate}));
};
